// The account part of the player screen's Settings tab (client/player-hub.ts): a private
// panel only the owner sees, sign-in and email, password and email changes, log out
// everywhere, and deleting the trainer (server/account-settings.ts). A trainer that comes
// back during its deletion grace period gets a card to keep it before anything else.
import { S } from './state.ts';
import { esc, full } from './fmt.ts';
import { openDialog, toast } from './dialog.ts';

interface AccountView {
  name: string; email: string; verified: boolean; hasPassword: boolean; providers: string[]; mail: boolean;
  created: number; deleteAt: number | null; market: { gemsEarned: number; creaturesSold: number };
}

let view: AccountView | null = null;
let loading = false;
let rerender: () => void = () => {};
export function bindAccountSettings(o: { rerender: () => void }) { rerender = o.rerender; }

const PROVIDER: Record<string, string> = { google: 'Google', apple: 'Apple', discord: 'Discord' };
const date = (ms: number) => new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

function token() { try { return localStorage.getItem('mi-token') ?? ''; } catch { return ''; } }

async function call(path: string, b?: Record<string, unknown>): Promise<{ ok: boolean; data: Record<string, unknown> }> {
  try {
    const res = await fetch(path, {
      method: b ? 'POST' : 'GET',
      headers: { authorization: `Bearer ${token()}`, ...(b ? { 'content-type': 'application/json' } : {}) },
      ...(b ? { body: JSON.stringify(b) } : {}),
    });
    return { ok: res.ok, data: await res.json().catch(() => ({})) };
  } catch {
    return { ok: false, data: { error: 'Could not reach the server.' } };
  }
}

export async function loadAccount() {
  if (loading) return;
  loading = true;
  const r = await call('/api/account');
  loading = false;
  if (r.ok) view = r.data as unknown as AccountView;
  rerender();
  if (view?.deleteAt) showHold();
}

/** Called once per page load after the socket says hello: a trainer set for deletion sees the keep card first. */
let checked = false;
export function checkAccountOnHello() { if (!checked) { checked = true; void loadAccount(); } }

// ---------------- the Settings tab ----------------
const row = (label: string, control: string, note = '') => `<div class="ph-set"><div><b>${label}</b>${note ? `<p class="muted">${note}</p>` : ''}</div><div class="ph-ctl">${control}</div></div>`;

/** Time played as "254 h 05 min": the Settings tab and the Overview's Lifetime card. */
export function playTime(ms: number) {
  const m = Math.floor(ms / 60_000);
  return m < 60 ? `${m} min` : `${full(Math.floor(m / 60))} h ${String(m % 60).padStart(2, '0')} min`;
}

/** Private totals: only the owner ever sees these. */
export function privatePanel(): string {
  const st = S.trainer!.stats;
  const v = view;
  const tile = (value: string, label: string, note = '') => `<div class="ph-tile"><span>${label}</span><b>${value}</b>${note ? `<em>${note}</em>` : ''}</div>`;
  return `<section class="panel ac-private"><div class="ph-h"><h4>Your account</h4><span class="ac-only">Only you</span></div><div class="ph-tiles">
    ${tile(v ? date(v.created) : '…', 'Trainer since')}
    ${tile(st.playMs ? playTime(st.playMs) : '—', 'Time played', st.playSince ? `since ${date(st.playSince)}` : '')}
    ${tile(v ? full(v.market.gemsEarned) : '…', 'Gems earned', 'from sales')}
    ${tile(v ? full(v.market.creaturesSold) : '…', 'Creatures sold')}
  </div></section>`;
}

export function accountPanel(admin: string): string {
  if (!view) { if (!loading) void loadAccount(); return `<section class="panel"><h4>Account</h4><p class="muted">Loading…</p></section>`; }
  const v = view;
  const ways = [v.hasPassword ? 'Email and password' : '', ...v.providers.map((p) => PROVIDER[p] ?? p)].filter(Boolean).join(' · ') || '—';
  const noMail = v.mail ? '' : ' disabled title="Email is not set up yet"';
  return `<section class="panel ac-acct"><h4>Account</h4>
    ${row('Trainer name', `<b>${esc(v.name)}</b>`)}
    ${row('Signs in with', `<span>${esc(ways)}</span>`)}
    ${row('Email', `<span class="ac-mail">${esc(v.email)}</span>${v.verified ? '<span class="ac-ok">confirmed</span>' : v.mail ? '<button class="btn tiny" data-act="ph-ac-verify">Confirm</button>' : '<span class="ac-no">unconfirmed</span>'}<button class="btn small" data-act="ph-ac-email"${noMail}>Change</button>`)}
    ${row('Password', v.hasPassword ? '<button class="btn small" data-act="ph-ac-pass">Change</button>' : `<button class="btn small" data-act="ph-ac-setpass"${noMail}>Set a password</button>`)}
    ${row('Chat filter', `<input type="checkbox" class="toggle" data-setting-bool="chatFilter" aria-label="Chat filter" title="Shows swearing as ***"${S.trainer?.settings.chatFilter !== false ? ' checked' : ''}>`)}
    ${admin}
    ${row('Log out', '<span class="ac-btns"><button class="btn small" data-act="ph-ac-logoutall">Everywhere</button><button class="btn small danger-outline" data-act="logout">Log out</button></span>')}
    <div class="ac-danger"><h5>Danger zone</h5>
      ${v.deleteAt
        ? row(`Deletion on ${date(v.deleteAt)}`, '<button class="btn small primary" data-act="ph-ac-keep">Keep my trainer</button>')
        : row('Delete my trainer', '<button class="btn small danger" data-act="ph-ac-delete">Delete</button>')}
    </div>
  </section>`;
}

// ---------------- actions ----------------
async function done(r: { ok: boolean; data: Record<string, unknown> }, okMsg = '') {
  const msg = String((r.ok ? r.data.msg : r.data.error) ?? '') || (r.ok ? okMsg : 'Something went wrong.');
  if (msg) toast(msg, r.ok ? '' : 'err');
  if (r.ok) await loadAccount();
  return r.ok;
}

const input = (name: string, type: string, label: string, extra = '') => `<label class="ac-field"><span>${label}</span><input name="${name}" type="${type}" ${extra}></label>`;
const errLine = (e: string) => (e ? `<p class="ac-err">${esc(e)}</p>` : '');

async function changePassword(err = '') {
  const d = await openDialog({
    title: 'Change password',
    body: `${input('current', 'password', 'Current password', 'autocomplete="current-password"')}${input('next', 'password', 'New password', 'autocomplete="new-password" minlength="8"')}${errLine(err)}`,
    actions: [{ label: 'Change password', value: 'go', kind: 'primary' }],
  });
  if (d.value !== 'go') return;
  const r = await call('/api/account/password', { current: d.data.current, next: d.data.next });
  if (!r.ok) return changePassword(String(r.data.error ?? 'Something went wrong.'));
  await done(r);
}

async function changeEmail(err = '', email = '') {
  const v = view!;
  const d = await openDialog({
    title: 'Change email',
    body: `${input('email', 'email', 'New email', `autocomplete="email" value="${esc(email)}"`)}${v.hasPassword ? input('password', 'password', 'Password', 'autocomplete="current-password"') : ''}${errLine(err)}`,
    actions: [{ label: 'Send link', value: 'go', kind: 'primary' }],
  });
  if (d.value !== 'go') return;
  const r = await call('/api/account/email', { email: d.data.email, password: d.data.password ?? '' });
  if (!r.ok) return changeEmail(String(r.data.error ?? 'Something went wrong.'), d.data.email);
  await done(r);
}

async function deleteTrainer(err = '') {
  const v = view!;
  const d = await openDialog({
    title: 'Delete your trainer',
    danger: true,
    body: `<p class="ac-lead">Everything is deleted in <b>14 days</b>. Log in before then to keep it.</p>
      ${input('name', 'text', `Type <b>${esc(v.name)}</b> to confirm`, 'autocomplete="off" spellcheck="false"')}${v.hasPassword ? input('password', 'password', 'Password', 'autocomplete="current-password"') : ''}${errLine(err)}`,
    actions: [{ label: 'Delete my trainer', value: 'go', kind: 'danger', disabled: true }],
    onInput: (root, data) => {
      const btn = root.querySelector<HTMLButtonElement>('[data-dlg="go"]');
      if (btn) btn.disabled = data.name.trim().toLowerCase() !== v.name.toLowerCase() || (v.hasPassword && !data.password);
    },
  });
  if (d.value !== 'go') return;
  const r = await call('/api/account/delete', { name: d.data.name, password: d.data.password ?? '' });
  if (!r.ok) return deleteTrainer(String(r.data.error ?? 'Something went wrong.'));
  // Every session ended: back to the login screen.
  try { localStorage.removeItem('mi-token'); } catch { /* storage off */ }
  location.reload();
}

async function keep() {
  const r = await call('/api/account/keep', {});
  hold?.remove(); hold = null;
  await done(r);
}

/** Clicks the hub hands over (data-act ph-ac-*). True when handled. */
export function accountClick(act: string): boolean {
  switch (act) {
    case 'ph-ac-pass': void changePassword(); return true;
    case 'ph-ac-setpass': void call('/api/account/set-password', {}).then((r) => done(r)); return true;
    case 'ph-ac-verify': void call('/api/account/verify', {}).then((r) => done(r)); return true;
    case 'ph-ac-email': void changeEmail(); return true;
    case 'ph-ac-delete': void deleteTrainer(); return true;
    case 'ph-ac-keep': void keep(); return true;
    case 'ph-ac-logoutall':
      void openDialog({ title: 'Log out everywhere?', body: '<p class="ac-lead">Every device, this one included, needs to log in again.</p>', actions: [{ label: 'Log out everywhere', value: 'go', kind: 'primary' }] })
        .then(async (d) => {
          if (d.value !== 'go') return;
          await call('/api/account/logout-all', {});
          try { localStorage.removeItem('mi-token'); } catch { /* storage off */ }
          location.reload();
        });
      return true;
  }
  return false;
}

// ---------------- the keep card ----------------
let hold: HTMLElement | null = null;
function showHold() {
  if (hold || !view?.deleteAt) return;
  hold = document.createElement('div');
  hold.className = 'scrim ac-hold';
  hold.innerHTML = `<div class="dialog" role="dialog" aria-modal="true" aria-labelledby="ac-hold-t">
    <h3 id="ac-hold-t">${esc(view.name)} is set to be deleted</h3>
    <div class="body"><p class="ac-lead">On <b>${date(view.deleteAt)}</b> this trainer and everything it owns is deleted for good.</p></div>
    <div class="actions"><button class="btn" data-hold="out">Log out</button><button class="btn primary" data-hold="keep">Keep my trainer</button></div></div>`;
  hold.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest('[data-hold]') as HTMLElement | null;
    if (!b) return;
    if (b.dataset.hold === 'keep') void keep();
    else { try { localStorage.removeItem('mi-token'); } catch { /* storage off */ } location.reload(); }
  });
  document.body.appendChild(hold);
  hold.querySelector<HTMLButtonElement>('[data-hold="keep"]')?.focus();
}
